'use client';

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  Avatar,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownSection,
  DropdownItem,
  addToast,
} from '@heroui/react';
import Link from 'next/link';
import { IoMdArrowDropright } from 'react-icons/io';
import { FaUser, FaBuilding } from 'react-icons/fa';
import { FaShield } from 'react-icons/fa6';
import { SessionType } from '@/util/types/globalTypes';
import { useGlobalContext } from '@/app/tools/globalContext';
import { MembershipWithCompanyType } from '@/util/types/serverTypeCollection/membership';
import { FaCheck } from 'react-icons/fa6';
import { signOut } from '@/lib/auth-client';
import {
  authSessionLogContext,
  authSignOut,
} from '@/util/functions/observability/frontendLogger';
import { switchCompanyAction } from '@/actions/switchCompany';
import { IoLogOutOutline } from 'react-icons/io5';
import { useClientMounted } from '@/util/hooks/useClientMounted';

const DROPDOWN_CN = {
  content:
    'max-w-[min(20rem,calc(100vw-1rem))] overflow-y-auto max-md:max-h-[var(--nav-user-menu-max-h,calc(100dvh-1rem))] md:max-h-[calc(100dvh-1rem)]',
};
const MENU_CN = { base: 'min-h-0 overflow-y-auto', list: 'min-h-0 overflow-y-auto' };

function useNavUserDropdownLayout(mobileMenuOpen: boolean) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [placement, setPlacement] = useState<'right' | 'top-start'>('right');
  const [style, setStyle] = useState<CSSProperties>();

  const sync = useCallback(() => {
    const mobile = window.matchMedia('(max-width: 767px)').matches;
    setPlacement(mobile ? 'top-start' : 'right');
    if (!mobile || !triggerRef.current) {
      setStyle(undefined);
      return;
    }
    const top = triggerRef.current.getBoundingClientRect().top;
    setStyle({ '--nav-user-menu-max-h': `${Math.max(160, top - 12)}px` } as CSSProperties);
  }, []);

  useEffect(() => {
    sync();
    const mq = window.matchMedia('(max-width: 767px)');
    mq.addEventListener('change', sync);
    window.addEventListener('resize', sync);
    return () => {
      mq.removeEventListener('change', sync);
      window.removeEventListener('resize', sync);
    };
  }, [sync, mobileMenuOpen]);

  return { placement, triggerRef, style, sync };
}

export default function NavUser({ session }: { session: SessionType }) {
  const { mobileMenuOpen } = useGlobalContext();
  const mounted = useClientMounted();
  const { placement, triggerRef, style, sync } =
    useNavUserDropdownLayout(mobileMenuOpen);
  const usesDropdown =
    (session?.user.memberships?.length ?? 0) > 1 ||
    Boolean(session?.user.isAttikAdmin);

  // console.log({ session });

  const body = (
    <>
      <div className='mx-1'>
        <Avatar
          size='sm'
          alt={session?.user?.firstName || 'User Avatar'}
          src={session.user?.employee?.thumb || session?.user?.picture}
          className='h-6 w-6'
        />
      </div>
      <p
        className={`
         flex items-center opacity-0
         transition-opacity duration-500 group-hover/navbar:opacity-100
        ${mobileMenuOpen ? 'opacity-100!' : ''}
      `}
      >
        {session?.user?.firstName
          ? `${session?.user?.firstName} ${session?.user?.lastName}`
          : 'User Name'}
        <IoMdArrowDropright size={'1.3rem'} />
      </p>
    </>
  );

  const handleMembershipChange = async (
    membership: MembershipWithCompanyType
  ) => {
    try {
      await switchCompanyAction(membership._companyId);
      localStorage.setItem('company-switch-trigger', Date.now().toString());
      window.location.reload();
    } catch (error) {
      console.error(error);
      addToast({
        title: 'Error',
        description: 'Failed to switch company',
        color: 'danger',
      });
    }
  };

  if (!mounted && usesDropdown) {
    return (
      <div className='flex w-full cursor-default items-center justify-start whitespace-nowrap py-1 outline-none hover:text-white'>
        {body}
      </div>
    );
  }

  if (session?.user.memberships.length > 1) {
    const sortedMemberships = [...session.user.memberships].sort((a, b) => {
      const nameA = (a.company?.name || '').toLowerCase();
      const nameB = (b.company?.name || '').toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });

    const switchCompanySection = (
      <DropdownSection title='Switch Company'>
        {sortedMemberships.map((membership) => {
          const companyName = membership.company?.name ?? 'Company';
          return (
            <DropdownItem
              key={membership._id}
              textValue={companyName}
              title={companyName}
              classNames={{ title: 'truncate' }}
              onPress={() => handleMembershipChange(membership)}
              startContent={
                membership._id === session.user.membership._id ? (
                  <FaCheck />
                ) : null
              }
            >
              {companyName}
            </DropdownItem>
          );
        })}
      </DropdownSection>
    );

    const accountSection = (
      <DropdownSection title='Account'>
        <DropdownItem
          key='account'
          href='/settings/account'
          as={Link}
          startContent={<FaUser />}
        >
          View Account
        </DropdownItem>
        <DropdownItem
          key='company'
          href='/settings/company'
          as={Link}
          startContent={<FaBuilding />}
        >
          View Company
        </DropdownItem>
      </DropdownSection>
    );

    const sessionSection = (
      <DropdownSection title='Session'>
        <DropdownItem
          key='signout'
          onPress={() =>
            signOut({
              fetchOptions: {
                onSuccess: () => {
                  window.location.href = '/login';
                },
              },
            })
          }
          startContent={<IoLogOutOutline />}
          color='danger'
          className='text-danger'
        >
          Sign Out
        </DropdownItem>
      </DropdownSection>
    );

    return (
      <Dropdown
        placement={placement}
        classNames={DROPDOWN_CN}
        style={style}
        onOpenChange={(open) => open && sync()}
      >
        <DropdownTrigger>
          <button
            ref={triggerRef}
            className='flex w-full items-center justify-start whitespace-nowrap py-1 outline-none hover:text-white'
          >
            {body}
          </button>
        </DropdownTrigger>
        {session.user.isAttikAdmin ? (
          <DropdownMenu
            disabledKeys={[session.user.membership._id]}
            classNames={MENU_CN}
          >
            {switchCompanySection}
            {accountSection}
            <DropdownSection title='Platform'>
              <DropdownItem
                key='admin-portal'
                href={
                  '/api/auth/to-admin'
                }
                startContent={<FaShield className='text-amber-500' />}
                className='text-amber-600'
              >
                Attik Admin
              </DropdownItem>
            </DropdownSection>
            {sessionSection}
          </DropdownMenu>
        ) : (
          <DropdownMenu
            disabledKeys={[session.user.membership._id]}
            classNames={MENU_CN}
          >
            {switchCompanySection}
            {accountSection}
            {sessionSection}
          </DropdownMenu>
        )}
      </Dropdown>
    );
  }
  else if (session.user.isAttikAdmin)
    return (
      <Dropdown
        placement={placement}
        classNames={DROPDOWN_CN}
        style={style}
        onOpenChange={(open) => open && sync()}
      >
        <DropdownTrigger>
          <button
            ref={triggerRef}
            className='flex w-full items-center justify-start whitespace-nowrap py-1 outline-none hover:text-white'
          >
            {body}
          </button>
        </DropdownTrigger>
        <DropdownMenu classNames={MENU_CN}>
          <DropdownSection title='Account'>
            <DropdownItem
              key='account'
              href='/settings/account'
              as={Link}
              startContent={<FaUser />}
            >
              View Account
            </DropdownItem>
            <DropdownItem
              key='company'
              href='/settings/company'
              as={Link}
              startContent={<FaBuilding />}
            >
              View Company
            </DropdownItem>
          </DropdownSection>
          <DropdownSection title='Platform'>
            <DropdownItem
              key='admin-portal'
              href={
                '/api/auth/to-admin'
              }
              startContent={<FaShield className='text-amber-500' />}
              className='text-amber-600'
            >
              Attik Admin
            </DropdownItem>
          </DropdownSection>
          <DropdownSection title='Session'>
            <DropdownItem
              key='signout'
              onPress={() => {
                const authLogCtx = authSessionLogContext(session);
                signOut({
                  fetchOptions: {
                    onSuccess: () => {
                      authSignOut(authLogCtx);
                      window.location.href = '/login';
                    },
                  },
                });
              }}
              startContent={<IoLogOutOutline />}
              color='danger'
              className='text-danger'
            >
              Sign Out
            </DropdownItem>
          </DropdownSection>
        </DropdownMenu>
      </Dropdown>
    );
  else
    return (
      <Link
        href='/settings/account'
        className='flex w-full items-center justify-start whitespace-nowrap py-1 hover:text-white'
      >
        {body}
      </Link>
    );
}
